import { handleError } from "@app/util/handle-error";
import {
  CreateExternalDealFilterDTO,
  DeleteManyExternalDealFiltersDTO,
  DeleteManyExternalDealFiltersResponseDTO,
  DuplicateExternalDealFiltersDTO,
  ExternalDealFilterListResponseMvoDTO,
  ExternalDealFiltersApi,
  GetExternalDealFiltersSortByEnum,
  GetExternalDealFiltersSortOrderEnum,
  InExternalDealFilterDTO,
  OutExternalDealFilterDTO,
  PatchManyExternalDealFiltersDTO,
  PatchManyExternalDealFiltersLiveDTO,
  PatchManyExternalDealFiltersSearchQueryDTO
} from "@app/api/generated";
import { getAuthorizedApiConfig } from "@app/util/api-config";
import { AppThunk } from "@app/redux/store";
import { FilterItem } from "@app/api/core/filter/filter-item";

import { ExternalDealFilterLiveDeals, externalDealFiltersActions } from "../reducers/external-deal-filters";
import { toast } from "react-toastify";

export interface IExternalDealFilterListOptions {
  ean?: string;
  liveDeals?: ExternalDealFilterLiveDeals;
  idSortOrder?: GetExternalDealFiltersSortOrderEnum;
}

const externalDealFiltersFiltersToJsonString = (
  searchQuery?: string,
  merchantId?: number,
  platformId?: number,
  status?: boolean,
  ean?: string,
  liveDeals?: ExternalDealFilterLiveDeals
): string =>
  JSON.stringify({
    searchQuery,
    platformId,
    merchantId,
    status,
    ean,
    liveDeals
  });

const getExternalDealFilters =
  (
    skip: number,
    take: number,
    merchantFilter?: FilterItem,
    platformId?: number,
    searchQuery?: string,
    status?: number,
    options: IExternalDealFilterListOptions = {}
  ): AppThunk =>
  async (dispatch): Promise<void> => {
    try {
      const api = new ExternalDealFiltersApi(await getAuthorizedApiConfig());
      dispatch(externalDealFiltersActions.setIsLoading(true));

      const filterQuery = externalDealFiltersFiltersToJsonString(
        searchQuery,
        merchantFilter?.value,
        platformId,
        !status ? undefined : status === 1 ? true : false,
        options.ean,
        options.liveDeals
      );

      const resultFromApi = await api.getExternalDealFilters({
        filterQuery,
        take,
        skip,
        sortBy: options.idSortOrder ? GetExternalDealFiltersSortByEnum.id : undefined,
        sortOrder: options.idSortOrder
      });

      dispatch(externalDealFiltersActions.setExternalDealFilters(resultFromApi.externalDealFilters || []));
      dispatch(externalDealFiltersActions.setTotalResults(resultFromApi.total));
      dispatch(externalDealFiltersActions.setTotalPages(Math.ceil(resultFromApi.total / take)));
      dispatch(externalDealFiltersActions.setIsLoading(false));
    } catch (error) {
      handleError(error, dispatch, "Failed to get external deal filters");
    }
  };

const getExternalDealFilter =
  (id: number): AppThunk =>
  async (dispatch): Promise<OutExternalDealFilterDTO | undefined> => {
    try {
      const api = new ExternalDealFiltersApi(await getAuthorizedApiConfig());

      const resultFromApi = await api.getExternalDealFilterById({ externalDealFilterId: id });

      dispatch(externalDealFiltersActions.setSelectedExternalDealFilter(resultFromApi));

      return resultFromApi;
    } catch (error) {
      handleError(error, dispatch, "Failed to get external deal filter");

      return undefined;
    }
  };

const deleteExternalDealFilter =
  (id: number): AppThunk =>
  async (dispatch): Promise<void> => {
    try {
      const api = new ExternalDealFiltersApi(await getAuthorizedApiConfig());

      const isDeleted = await api.deleteExternalDealFilter({ externalDealFilterId: id });

      if (isDeleted) {
        dispatch(externalDealFiltersActions.deleteExternalDealFilterFromList(id));
      }
    } catch (error) {
      handleError(error, dispatch, "Failed to delete external deal filter");
      // new FatalError(error.name, error.message, error.stack);
    }
  };

const createExternalDealFilter =
  (externalDealFilter: CreateExternalDealFilterDTO): AppThunk =>
  async (dispatch): Promise<OutExternalDealFilterDTO | undefined> => {
    try {
      const api = new ExternalDealFiltersApi(await getAuthorizedApiConfig());
      const createdExternalDealFilter = await api.createExternalDealFilter({
        createExternalDealFilterDTO: externalDealFilter
      });

      dispatch(externalDealFiltersActions.setSelectedExternalDealFilter(createdExternalDealFilter));

      return createdExternalDealFilter;
    } catch (error) {
      handleError(error, dispatch, "Failed to create new external deal filter");
    }

    return undefined;
  };

const duplicateExternalDealFilters =
  (filters: DuplicateExternalDealFiltersDTO): AppThunk =>
  async (dispatch): Promise<ExternalDealFilterListResponseMvoDTO | undefined> => {
    try {
      const api = new ExternalDealFiltersApi(await getAuthorizedApiConfig());
      const createdExternalDealFilters = await api.duplicateManyExternalDealFilter({
        duplicateExternalDealFiltersDTO: filters
      });

      return createdExternalDealFilters;
    } catch (error) {
      handleError(error, dispatch, "Failed to duplicate external deal filters.");
    }

    return undefined;
  };

const saveExternalDealFilter =
  (externalDealFilter: InExternalDealFilterDTO): AppThunk =>
  async (dispatch): Promise<OutExternalDealFilterDTO | undefined> => {
    try {
      const api = new ExternalDealFiltersApi(await getAuthorizedApiConfig());
      const savedExternalDealFilter = await api.putExternalDealFilter({
        externalDealFilterId: externalDealFilter.id,
        inExternalDealFilterDTO: externalDealFilter
      });

      dispatch(externalDealFiltersActions.setSelectedExternalDealFilter(savedExternalDealFilter));

      return savedExternalDealFilter;
    } catch (error) {
      handleError(error, dispatch, "Failed to save external deal filter");
    }
  };

const patchRemoveRelationsToManyEdfs =
  (externalDealFilters: PatchManyExternalDealFiltersDTO): AppThunk =>
  async (dispatch): Promise<ExternalDealFilterListResponseMvoDTO | undefined> => {
    try {
      const api = new ExternalDealFiltersApi(await getAuthorizedApiConfig());
      const savedExternalDealFilters = await api.patchManyEdfsRemoveMerchantsAndCategories({
        patchManyExternalDealFiltersDTO: externalDealFilters
      });

      toast.success("De filters zijn aangepast.");
      return savedExternalDealFilters;
    } catch (error) {
      handleError(error, dispatch, "Failed to save external deal filter");
    }
  };

const patchAddRelationsToManyEdfs =
  (externalDealFilters: PatchManyExternalDealFiltersDTO): AppThunk =>
  async (dispatch): Promise<ExternalDealFilterListResponseMvoDTO | undefined> => {
    try {
      const api = new ExternalDealFiltersApi(await getAuthorizedApiConfig());
      const savedExternalDealFilters = await api.patchManyEdfsAddMerchantsAndCategories({
        patchManyExternalDealFiltersDTO: externalDealFilters
      });

      toast.success("De filters zijn aangepast.");
      return savedExternalDealFilters;
    } catch (error) {
      handleError(error, dispatch, "Failed to save external deal filter");
    }
  };

const patchLiveManyEdfs =
  (externalDealFilters: PatchManyExternalDealFiltersLiveDTO): AppThunk =>
  async (dispatch): Promise<ExternalDealFilterListResponseMvoDTO | undefined> => {
    try {
      const api = new ExternalDealFiltersApi(await getAuthorizedApiConfig());
      const savedExternalDealFilters = await api.patchSetManyEdfOnlineOrOffline({
        patchManyExternalDealFiltersLiveDTO: externalDealFilters
      });

      toast.success("De filters zijn aangepast.");
      return savedExternalDealFilters;
    } catch (error) {
      handleError(error, dispatch, "Failed to save external deal filter");
    }
  };

const deleteManyExternalDealFilters =
  (filters: DeleteManyExternalDealFiltersDTO): AppThunk =>
  async (dispatch): Promise<DeleteManyExternalDealFiltersResponseDTO | undefined> => {
    try {
      const api = new ExternalDealFiltersApi(await getAuthorizedApiConfig());
      const result = await api.deleteManyExternalDealFilters({
        deleteManyExternalDealFiltersDTO: filters
      });

      toast.success(
        result.dealsTakenOffline > 0
          ? `${result.deleted} filters verwijderd, ${result.dealsTakenOffline} deals offline gezet.`
          : `${result.deleted} filters verwijderd.`
      );
      return result;
    } catch (error) {
      handleError(error, dispatch, "Failed to delete external deal filters");
    }
  };

const patchSearchQueryManyEdfs =
  (externalDealFilters: PatchManyExternalDealFiltersSearchQueryDTO): AppThunk =>
  async (dispatch): Promise<ExternalDealFilterListResponseMvoDTO | undefined> => {
    try {
      const api = new ExternalDealFiltersApi(await getAuthorizedApiConfig());
      const savedExternalDealFilters = await api.patchManyEdfsSearchQuery({
        patchManyExternalDealFiltersSearchQueryDTO: externalDealFilters
      });

      toast.success("De zoekterm van de filters is aangepast.");
      return savedExternalDealFilters;
    } catch (error) {
      handleError(error, dispatch, "Failed to save external deal filters");
    }
  };

export const externalDealFiltersThunk = {
  getExternalDealFilters,
  getExternalDealFilter,
  deleteExternalDealFilter,
  createExternalDealFilter,
  saveExternalDealFilter,
  patchAddRelationsToManyEdfs,
  patchRemoveRelationsToManyEdfs,
  duplicateExternalDealFilters,
  patchLiveManyEdfs,
  deleteManyExternalDealFilters,
  patchSearchQueryManyEdfs
};
